'use strict';

/**
 * CandidateSearch
 * ---------------
 * A reusable server-backed candidate search for record pickers (WI #445 -- split 1 of #434). A picker
 * that builds its options from a provider's store().List only ever offers the ONE PAGE that store holds
 * (WI #433); the Blueprint "pin an item" picker is the worst case, because it has a search box that
 * types like a live search and only ever searches what happens to be loaded. A miss then reads as "that
 * record does not exist" rather than "that record is not loaded". Prod holds 1,075 blueprints.
 *
 * THE TRAP THIS EXISTS TO AVOID (see FocusQuery.js's own note). foxhound quotes whatever field name it
 * is handed, so a filter built against a column an entity does not have compiles to a query Postgres
 * refuses outright -- a 500 on the first keystroke, on nearly every entity in the app. There is exactly
 * one column every entity descriptor DECLARES for this purpose: TitleField, the same field
 * PictView-EntityDetail._label() and _rebuild() already read to show a row's name. So this searches that
 * column, and only that column -- declared, not guessed.
 *
 * resolve(), NOT list(). Pict-Provider-EntityAPI.resolve() asks the server the identical
 * Page/PageSize/Filter question list() does, but merges the rows into the provider's Index alone,
 * leaving List/Page/PageCount/QueryKey untouched (WI #447). A picker mounted on a page that also hosts a
 * list screen -- exactly the Blueprint detail page -- must not have its search box quietly reset that
 * screen back to page one every time someone types a letter.
 *
 * Wires directly onto pict-section-picker's DataProvider/ResolveValue contract, so a caller gets server
 * search, built-in debounce, and "load more" paging for free:
 *
 *   mountPicker('#host', 'MyPicker', [], onChange,
 *     {
 *       DataProvider: libCandidateSearch.dataProviderFor(provider, descriptor),
 *       ResolveValue: libCandidateSearch.resolveValueFor(provider, descriptor)
 *     });
 *
 * WI #446 adds two things the seven remaining call sites needed that the Blueprint pin picker (WI #445)
 * did not:
 *
 *   - An exclude list (dataProviderFor's 4th arg): an "add a link" picker must not offer a record that
 *     is already linked, or (a self-referencing relation, e.g. Related visions) the record itself. The
 *     old code filtered these out of the loaded page client-side; a server search still can, by id,
 *     against whatever page comes back.
 *   - Multi-type search (searchTypes/dataProviderForTypes/resolveValueForTypes): the "spawn work item"
 *     target picker searches Feature, Vision and Module at once, Value carrying the type ("Feature:12")
 *     the way its precomputed Options list always did. Each type is searched independently against its
 *     own TitleField and merged, so this is still one column per query, never a guess.
 */

// pict-section-picker's own default (see its README); kept in step rather than re-derived per caller.
const _PAGE_SIZE = 20;

/** Tildes separate FocusQuery-style stanzas; a search term carrying one would split into a different filter. */
function _safe(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/~/g, ' ');
}

/**
 * One page of candidates for pEntity, filtered to pTerm against its descriptor-declared TitleField.
 *
 * @param {object} pProvider    A Pict-Provider-EntityAPI instance (or subclass) -- must expose resolve().
 * @param {object} pDescriptor  The entity's EntityDescriptors.Entities[...] entry.
 * @param {string} pTerm        The search box text. Empty returns the first page, unfiltered.
 * @param {number} pPage        Zero-based page index (pict-section-picker's DataProvider convention).
 * @param {number} [pPageSize]  Rows per page. Defaults to pict-section-picker's own default.
 * @param {string[]} [pExcludeIDs]  Ids to drop from the results (already linked, or the record itself).
 * @return {Promise<{results: Array<{Value:string, Text:string}>, hasMore: boolean}>}
 */
function search(pProvider, pDescriptor, pTerm, pPage, pPageSize, pExcludeIDs)
{
	let tmpDescriptor = pDescriptor || {};
	let tmpTitleField = tmpDescriptor.TitleField;
	let tmpIDField = tmpDescriptor.IDField;
	// No provider, or a descriptor that (unusually) declares neither field: there is nothing safe to ask
	// the server, so answer "nothing found" rather than guess a column name and risk the 500 above.
	if (!pProvider || (typeof pProvider.resolve !== 'function') || !tmpTitleField || !tmpIDField)
	{
		return Promise.resolve({ results: [], hasMore: false });
	}
	let tmpPageSize = Math.max(1, Number(pPageSize) || _PAGE_SIZE);
	let tmpTerm = String(pTerm || '').trim();
	let tmpFilter = tmpTerm ? ('FBV~' + tmpTitleField + '~LK~%' + _safe(tmpTerm) + '%') : '';
	// DataProvider's page is zero-based; the provider's own Page option is one-based (matches its list()).
	let tmpPage = Math.max(1, (Number(pPage) || 0) + 1);
	let tmpExclude = Array.isArray(pExcludeIDs) ? pExcludeIDs.map(String) : [];
	return pProvider.resolve({ Page: tmpPage, PageSize: tmpPageSize, Filter: tmpFilter })
		.then((pRows) =>
		{
			let tmpRows = Array.isArray(pRows) ? pRows : [];
			let tmpResults = tmpRows
				.filter((pRow) => tmpExclude.indexOf(String(pRow[tmpIDField])) < 0)
				.map((pRow) =>
					({ Value: String(pRow[tmpIDField]), Text: pRow[tmpTitleField] || ('#' + pRow[tmpIDField]) }));
			// resolve() carries no server Total (that is the point of it -- WI #447). A full page is the
			// same "there may be more" signal EntityAPI._listAll's own short-page stop condition uses,
			// read the other way round: a page that came back short is the last one. (The exclude filter
			// runs after that check, so a page shortened only by exclusions still offers a next page.)
			return { results: tmpResults, hasMore: tmpRows.length >= tmpPageSize };
		});
}

/** Bind a ready-to-use DataProvider closure for one entity -- hand straight to mountPicker/createPicker. */
function dataProviderFor(pProvider, pDescriptor, pPageSize, pExcludeIDs)
{
	return function (pTerm, pPage) { return search(pProvider, pDescriptor, pTerm, pPage, pPageSize, pExcludeIDs); };
}

/**
 * Resolve the display label for a value a picker was mounted with (a pending selection carried across a
 * re-render). Checks the provider's own Index first -- resolve()/cacheRecord() already merge search
 * results there -- and only reaches for a direct read() when this id never came back from a search.
 */
function resolveValueFor(pProvider, pDescriptor)
{
	return function (pValue)
	{
		let tmpDescriptor = pDescriptor || {};
		let tmpStore = (pProvider && typeof pProvider.store === 'function') ? pProvider.store() : null;
		let tmpCached = (tmpStore && tmpStore.Index) ? tmpStore.Index[pValue] : null;
		if (tmpCached) { return Promise.resolve({ Value: String(pValue), Text: tmpCached[tmpDescriptor.TitleField] || ('#' + pValue) }); }
		if (!pProvider || (typeof pProvider.read !== 'function')) { return Promise.resolve({ Value: String(pValue), Text: '#' + pValue }); }
		return pProvider.read(pValue).then((pRecord) =>
			({ Value: String(pValue), Text: (pRecord && pRecord[tmpDescriptor.TitleField]) || ('#' + pValue) }));
	};
}

/**
 * The same search, fanned out across several entity types at once and merged into one result list --
 * the "spawn a work item" target picker's shape (Feature, Vision or Module, one combined list). Each
 * type is queried for the same page/term independently against its OWN TitleField; Value carries the
 * type so the caller can split it back out ("Feature:12"), matching the precomputed-Options format this
 * replaces.
 *
 * @param {Array<{Type:string, Provider:object, Descriptor:object}>} pTypes
 * @param {string} pTerm
 * @param {number} pPage
 * @param {number} [pPageSizePerType]  Rows per page, PER TYPE (so 3 types page 20 each, not 20 total).
 * @return {Promise<{results: Array<{Value:string, Text:string}>, hasMore: boolean}>}
 */
function searchTypes(pTypes, pTerm, pPage, pPageSizePerType)
{
	let tmpTypes = Array.isArray(pTypes) ? pTypes : [];
	return Promise.all(tmpTypes.map((pType) =>
		search(pType.Provider, pType.Descriptor, pTerm, pPage, pPageSizePerType).then((pResult) =>
			({
				hasMore: pResult.hasMore,
				results: pResult.results.map((pRow) =>
					({ Value: pType.Type + ':' + pRow.Value, Text: pRow.Text + '  -  ' + pType.Type }))
			}))))
		.then((pPerType) =>
			({
				results: pPerType.reduce((pAcc, pOne) => pAcc.concat(pOne.results), []),
				hasMore: pPerType.some((pOne) => pOne.hasMore)
			}));
}

/** Bind a ready-to-use multi-type DataProvider closure -- hand straight to mountPicker/createPicker. */
function dataProviderForTypes(pTypes, pPageSizePerType)
{
	return function (pTerm, pPage) { return searchTypes(pTypes, pTerm, pPage, pPageSizePerType); };
}

/** resolveValueFor's multi-type counterpart: pValue is "Type:id", routed to that type's own resolver. */
function resolveValueForTypes(pTypes)
{
	return function (pValue)
	{
		let tmpParts = String(pValue || '').split(':');
		let tmpType = tmpParts[0];
		let tmpID = tmpParts[1];
		let tmpMatch = (Array.isArray(pTypes) ? pTypes : []).find((pType) => pType.Type === tmpType);
		if (!tmpMatch) { return Promise.resolve({ Value: String(pValue), Text: '#' + pValue }); }
		return resolveValueFor(tmpMatch.Provider, tmpMatch.Descriptor)(tmpID).then((pResolved) =>
			({ Value: String(pValue), Text: pResolved.Text + '  -  ' + tmpType }));
	};
}

module.exports =
{
	search, dataProviderFor, resolveValueFor,
	searchTypes, dataProviderForTypes, resolveValueForTypes,
	_safe, PAGE_SIZE: _PAGE_SIZE
};
